import { FiBook } from 'react-icons/fi';

export const awlSublist67Data = {
  courseId: 'awl-sublist-6-7',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublists 6-7',
  icon: FiBook,
  overview: {
    impact: 'Develop specialised academic vocabulary for Band 7+ performance',
    focus: '12 key words from Coxhead\'s AWL Sublists 6 and 7',
    uses: 'Reading comprehension, essay writing',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublists 6-7 contain more specialised academic vocabulary. These words are frequently found in research-oriented Reading passages and are particularly effective in sophisticated Writing Task 2 essays.</p>',
      points: [
        'These words signal a high level of academic English when used correctly',
        'They appear in IELTS Reading passages on research, policy, and social issues',
        'Using them accurately can push your Lexical Resource score towards Band 8',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['abstract', 'Existing in thought or as an idea; not concrete', 'Concepts like justice and freedom are highly abstract.'],
          ['acknowledge', 'To accept or recognise as true or existing', 'The government acknowledged that the policy had shortcomings.'],
          ['aggregate', 'A whole formed by combining several elements', 'The aggregate score is calculated from three separate tests.'],
          ['allocate', 'To distribute resources or duties for a particular purpose', 'The council allocated additional funding to public transport.'],
          ['assign', 'To give a task, duty, or role to someone', 'Each student was assigned a different research topic.'],
          ['attach', 'To fasten or join one thing to another; to attribute importance', 'Many cultures attach great significance to family traditions.'],
          ['bond', 'A connection or tie between people, groups, or substances', 'Education can strengthen the bond between communities.'],
          ['brief', 'Lasting only a short time; concise', 'The manager gave a brief overview of the quarterly results.'],
          ['capable', 'Having the ability or quality needed to do something', 'Modern technology is capable of transforming entire industries.'],
          ['cite', 'To quote or refer to as evidence or authority', 'The author cited several studies to support the argument.'],
          ['cooperate', 'To work together towards a shared goal', 'Nations must cooperate to address the challenges of climate change.'],
          ['discriminate', 'To recognise differences; to treat unfairly based on prejudice', 'It is illegal to discriminate against employees on the basis of gender.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Sublists 6-7 words have rich and sometimes surprising word families. Mastering all forms helps you demonstrate grammatical range and accuracy.</p>',
      points: [
        'Several words have both positive and negative connotations depending on form (e.g. "discriminate" vs "discriminating")',
        'Correct word-form usage avoids errors that lower your Grammar score',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['abstract', 'abstract, abstraction', 'abstract', 'abstract, abstractly'],
          ['acknowledge', 'acknowledgement', 'acknowledge', 'acknowledged'],
          ['aggregate', 'aggregate, aggregation', 'aggregate', 'aggregate'],
          ['allocate', 'allocation', 'allocate', 'allocated'],
          ['assign', 'assignment', 'assign', 'assigned'],
          ['attach', 'attachment', 'attach', 'attached, unattached'],
          ['bond', 'bond, bonding', 'bond', 'bonded'],
          ['brief', 'brief, briefing', 'brief', 'brief, briefly'],
          ['capable', 'capability', '—', 'capable, capably, incapable'],
          ['cite', 'citation', 'cite', 'cited'],
          ['cooperate', 'cooperation, cooperative', 'cooperate', 'cooperative, cooperatively'],
          ['discriminate', 'discrimination', 'discriminate', 'discriminatory, discriminating'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Using precise collocations from Sublists 6-7 signals a sophisticated command of academic English to IELTS examiners.</p>',
      points: [
        'These collocations are common in academic journals and IELTS source texts',
        'Memorising 2-3 per word gives you flexible options under exam pressure',
      ],
      examples: [
        { label: 'abstract', text: 'abstract concept, abstract thinking, abstract art, in the abstract' },
        { label: 'acknowledge', text: 'widely acknowledged, acknowledge the fact that, fail to acknowledge' },
        { label: 'allocate', text: 'allocate resources, allocate funding, allocate time, budget allocation' },
        { label: 'assign', text: 'assign a task, assign responsibility, assigned to, homework assignment' },
        { label: 'attach', text: 'attach importance to, attach significance to, deeply attached, attached file' },
        { label: 'bond', text: 'strong bond, form a bond, bond between, emotional bond' },
        { label: 'brief', text: 'brief overview, brief description, in brief, brief period' },
        { label: 'capable', text: 'capable of, highly capable, capable leader, capability to' },
        { label: 'cite', text: 'cite evidence, cite a source, cite an example, frequently cited' },
        { label: 'cooperate', text: 'cooperate with, cooperate closely, international cooperation, cooperative learning' },
        { label: 'discriminate', text: 'discriminate against, discriminate between, racial discrimination, gender discrimination' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Sublists 6-7 words are ideal for research-based discussions, opinion essays, and arguments about fairness and cooperation.</p>',
      points: [
        'Use "cite" when referring to evidence: "As cited by Smith (2020)..." or "Studies frequently cite..."',
        'Replace "work together" with "cooperate" and "give out" with "allocate" for academic register',
        'Use "attach importance to" instead of "think something is important" for sophisticated phrasing',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Many people think family traditions are very important.' },
        { label: 'Strong sentence', text: 'Many cultures attach great significance to family traditions and the bonds they foster.' },
        { label: 'Weak sentence', text: 'Countries should work together to fix environmental problems.' },
        { label: 'Strong sentence', text: 'Nations must cooperate closely to allocate resources effectively and address environmental challenges.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "allocate" mean?', options: ['To distribute for a particular purpose', 'To remove from a budget', 'To place in alphabetical order'], answer: 'To distribute for a particular purpose', explanation: '"Allocate" means to set aside or distribute resources, time, or money for a specific use.' },
        { text: 'What does "cite" mean?', options: ['To see from a distance', 'To quote or refer to as evidence', 'To place something on a website'], answer: 'To quote or refer to as evidence', explanation: '"Cite" means to refer to a source or give evidence, common in academic writing.' },
        { text: 'What does "discriminate" mean?', options: ['To treat unfairly based on prejudice', 'To distribute equally', 'To describe in detail'], answer: 'To treat unfairly based on prejudice', explanation: '"Discriminate against" means to treat someone unfairly. "Discriminate between" means to recognise differences.' },
        { text: 'What does "abstract" (adjective) mean?', options: ['Existing as a concrete object', 'Existing in thought; not physical or concrete', 'Very old and historical'], answer: 'Existing in thought; not physical or concrete', explanation: '"Abstract" describes ideas that exist theoretically, like justice, equality, or happiness.' },
        { text: 'What does "acknowledge" mean?', options: ['To accept or recognise something as true', 'To ignore deliberately', 'To question or doubt'], answer: 'To accept or recognise something as true', explanation: '"Acknowledge" means to accept the truth or existence of something, often reluctantly.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublists 6-7.',
      questions: [
        { text: 'The government must ___ sufficient funds to improve healthcare infrastructure.', answer: 'allocate', explanation: '"Allocate funds" is a standard collocation meaning to set aside money for a purpose.' },
        { text: 'International ___ is essential to combat global warming effectively.', answer: 'cooperation', explanation: '"International cooperation" is a key collocation for essays about global issues.' },
        { text: 'The researcher ___ three peer-reviewed studies to support her argument.', answer: 'cited', explanation: '"Cited studies" is academic language for referring to evidence — past tense here.' },
        { text: 'Education is ___ of transforming communities and reducing poverty.', answer: 'capable', explanation: '"Capable of" + gerund is the standard pattern, meaning having the ability to do something.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'The ___ of resources must be fair and transparent. (allocate)', options: ['allocation', 'allocate', 'allocated'], answer: 'allocation', explanation: '"The allocation of" requires the noun form after the article.' },
        { text: 'The company has a strict policy against ___ in hiring. (discriminate)', options: ['discriminate', 'discrimination', 'discriminatory'], answer: 'discrimination', explanation: '"Against discrimination" requires the noun form after the preposition.' },
        { text: 'The professor gave a ___ summary of the key points. (brief)', options: ['briefly', 'brief', 'briefing'], answer: 'brief', explanation: 'An adjective is needed before the noun "summary" — "brief" modifies it.' },
        { text: 'It is widely ___ that education reduces inequality. (acknowledge)', options: ['acknowledged', 'acknowledgement', 'acknowledge'], answer: 'acknowledged', explanation: '"It is widely acknowledged that..." is a common academic structure using the past participle.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "cooperate" about international relations.', example: 'Developed and developing nations must cooperate more effectively to achieve the UN Sustainable Development Goals.', tip: 'Use "cooperate with" or "cooperate closely" for natural academic phrasing.' },
        { text: 'Write a sentence using "bond" about family or community.', example: 'Shared cultural traditions help strengthen the bond between generations within a community.', tip: 'Try "strong bond", "form a bond", or "bond between" for natural collocations.' },
        { text: 'Write a sentence using "acknowledge" about a social issue.', example: 'Governments must acknowledge the growing impact of automation on employment before it becomes a crisis.', tip: 'Use "acknowledge that" + clause, or "widely acknowledged" for passive constructions.' },
      ],
    },
  ],
  tips: [
    '"Cite" is essential for academic writing — practise structures like "as cited by..." and "frequently cited"',
    'Learn the difference: "discriminate against" (unfair treatment) vs "discriminate between" (recognise differences)',
    '"Attach importance to" is a Band 8 phrase — use it instead of "think something is important"',
    '"Allocate" works for time, money, and resources — one of the most versatile Sublist 6 words',
  ],
  dailyPractice: [
    'Write an opinion paragraph using "acknowledge", "cite", and "cooperate"',
    'Read a research abstract and identify Sublists 6-7 vocabulary',
    'Practise the pattern "It is widely acknowledged that..." with 3 different topics',
    'Create sentences using "allocate", "assign", and "attach" to compare their usage',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublists 6-7 vocabulary course covering 12 key academic words: abstract, acknowledge, aggregate, allocate, assign, attach, bond, brief, capable, cite, cooperate, discriminate. Includes word meanings, word families, collocations, and IELTS Writing integration.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist67Data,
      title: awlSublist67Data.title + ' (Foundations)',
      overview: { ...awlSublist67Data.overview, duration: '15-20 minutes' },
      sections: awlSublist67Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist67Data.sections.length / 2))),
      exercises: awlSublist67Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist67Data,
      title: awlSublist67Data.title + ' (Advanced)',
      overview: { ...awlSublist67Data.overview, duration: '30-35 minutes' },
      sections: awlSublist67Data.sections,
      exercises: awlSublist67Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist67Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist67Data;
}
